"use client"

import { heroTypes } from "@/data/hero-types"
import type { HeroType } from "@/types/hero.types"

interface HeroSelectScreenProps {
  onSelectHero: (heroType: HeroType) => void
  selectedHero: HeroType | null
}

export const HeroSelectScreen = ({ onSelectHero, selectedHero }: HeroSelectScreenProps) => {
  const heroes = Object.values(heroTypes)

  return (
    <div
      id="hero-select-screen"
      className="fixed inset-0 bg-gradient-to-b from-background via-secondary to-background flex flex-col items-center justify-center p-4"
    >
      <h1 id="hero-select-title" className="text-3xl font-bold text-game-highlight mb-8 tracking-wide">
        Select Your Hero
      </h1>

      <div id="hero-select-grid" className="flex gap-6 mb-8">
        {heroes.map((hero) => (
          <button
            key={hero.id}
            id={`hero-select-${hero.id}`}
            onClick={() => onSelectHero(hero.id as HeroType)}
            className={`relative flex flex-col items-center p-4 rounded-lg border-2 transition-all duration-200 ${
              selectedHero === hero.id
                ? "border-white/80 bg-white/10 scale-105"
                : "border-game-border bg-secondary/50 hover:border-white/50 hover:bg-muted/50"
            }`}
          >
            {/* Portrait */}
            <div
              id={`hero-portrait-${hero.id}`}
              className="w-24 h-24 rounded-lg overflow-hidden border-2 border-game-border mb-3"
            >
              {hero.portrait ? (
                <img
                  src={hero.portrait || "/placeholder.svg"}
                  alt={hero.displayName || hero.name}
                  width={96}
                  height={96}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: "pixelated" }}
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-3xl font-bold"
                  style={{ backgroundColor: hero.color }}
                >
                  {hero.name[0]}
                </div>
              )}
            </div>

            {/* Hero Name */}
            <h2 id={`hero-name-${hero.id}`} className="text-lg font-bold text-foreground mb-1">
              {hero.displayName || hero.name}
            </h2>
            <p id={`hero-class-${hero.id}`} className="text-sm text-game-highlight mb-2">
              {hero.name}
            </p>

            {/* Stats Preview */}
            <div id={`hero-stats-${hero.id}`} className="text-xs text-muted-foreground space-y-1">
              <div className="flex justify-between gap-4">
                <span>Health:</span>
                <span className="text-game-health">{hero.baseStats.health}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span>Damage:</span>
                <span className="text-game-danger">
                  {hero.baseStats.damage.min}-{hero.baseStats.damage.max}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span>Range:</span>
                <span className="text-game-mana">{hero.baseStats.attackRange}</span>
              </div>
            </div>

            {/* Selection indicator */}
            {selectedHero === hero.id && (
              <div
                id={`hero-selected-indicator-${hero.id}`}
                className="absolute -top-2 -right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center"
              >
                <span className="text-black text-sm font-bold">✓</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Description */}
      {selectedHero && (
        <p id="hero-description" className="text-muted-foreground text-center max-w-md mb-6">
          {heroTypes[selectedHero]?.description}
        </p>
      )}

      {/* Confirm Button */}
      <button
        id="hero-confirm-button"
        onClick={() => selectedHero && onSelectHero(selectedHero)}
        disabled={!selectedHero}
        className={`px-8 py-3 rounded-lg font-bold text-lg transition-all duration-200 ${
          selectedHero
            ? "bg-game-highlight text-game-highlight-foreground hover:opacity-90 hover:scale-105"
            : "bg-muted text-muted-foreground cursor-not-allowed"
        }`}
      >
        Start Mission
      </button>
    </div>
  )
}
